/**
 * The two state machines the payment flow actually has — written down.
 *
 * v1 has both, implicitly: the session one is spread across
 * `checkoutModalState` / `isCheckoutActive` / `showPaymentModal` /
 * `paymentFlow` / `isLoading`, and the leg one across `paymentStatus`,
 * `posted`, `errorMessage` and the `res.state` switch that appears twice
 * (`handleGetExtCb` and `handleRefreshGetExtCb`). Booleans that can contradict
 * each other are how you get "Confirm is enabled while a payment is pending".
 *
 * Pure and framework-free on purpose: the tables below are assertable without
 * redux or React, and the same machine drives kiosk and oracle.
 */

/* ------------------------------------------------------------------ session */

export const SESSION = {
  /** no payment session — the modal is closed */
  IDLE: "idle",
  /** legs are being added / edited */
  COLLECTING: "collecting",
  /** an async leg is with a provider; CTA disabled, session cannot be closed */
  SETTLING: "settling",
  /** save/close is in flight */
  COMMITTING: "committing",
  /** persisted; the host tears the session down */
  COMMITTED: "committed",
  /** commit came back with an error — recoverable, back to collecting */
  FAILED: "failed",
} as const;
export type SessionState = (typeof SESSION)[keyof typeof SESSION];

export const SESSION_EVENT = {
  OPEN: "OPEN",
  EDIT: "EDIT",
  LEG_REQUESTED: "LEG_REQUESTED",
  LEG_RESOLVED: "LEG_RESOLVED",
  COMMIT: "COMMIT",
  COMMIT_OK: "COMMIT_OK",
  /** a SAVE came back — persisted, but the operator is still collecting */
  SAVED: "SAVED",
  COMMIT_ERR: "COMMIT_ERR",
  RETRY: "RETRY",
  CLOSE: "CLOSE",
} as const;
export type SessionEvent = (typeof SESSION_EVENT)[keyof typeof SESSION_EVENT];

const SESSION_TABLE: Record<SessionState, Partial<Record<SessionEvent, SessionState>>> = {
  [SESSION.IDLE]: {
    OPEN: SESSION.COLLECTING,
  },
  [SESSION.COLLECTING]: {
    EDIT: SESSION.COLLECTING,
    LEG_REQUESTED: SESSION.SETTLING,
    COMMIT: SESSION.COMMITTING,
    CLOSE: SESSION.IDLE,
  },
  [SESSION.SETTLING]: {
    // no EDIT and no COMMIT here — that is the invariant v1's booleans lose
    LEG_RESOLVED: SESSION.COLLECTING,
    LEG_REQUESTED: SESSION.SETTLING,
  },
  [SESSION.COMMITTING]: {
    COMMIT_OK: SESSION.COMMITTED,
    // A save is a WRITE, not an ending. v1 keeps the checkout open on this path
    // (`extSaveCallback:837` folds the response back in and returns to the mode
    // grid); routing it to COMMITTED instead would forbid EDIT and strand the
    // operator, which is what happened after every failed external payment.
    SAVED: SESSION.COLLECTING,
    COMMIT_ERR: SESSION.FAILED,
  },
  [SESSION.COMMITTED]: {
    CLOSE: SESSION.IDLE,
  },
  [SESSION.FAILED]: {
    RETRY: SESSION.COLLECTING,
    EDIT: SESSION.COLLECTING,
    CLOSE: SESSION.IDLE,
  },
};

/* ---------------------------------------------------------------------- leg */

export const LEG = {
  /** a local leg (cash/card/…) — terminal the moment it is entered */
  DRAFT: "draft",
  /** sent to the provider, awaiting a terminal status */
  REQUESTED: "requested",
  /** provider says "not yet" — keep polling */
  PENDING: "pending",
  POSTED: "posted",
  FAILED: "failed",
  CANCELLED: "cancelled",
  REFUNDED: "refunded",
} as const;
export type LegState = (typeof LEG)[keyof typeof LEG];

export const LEG_EVENT = {
  INITIATE: "INITIATE",
  POLL_PENDING: "POLL_PENDING",
  POLL_COMPLETED: "POLL_COMPLETED",
  POLL_DECLINED: "POLL_DECLINED",
  POLL_CANCELLED: "POLL_CANCELLED",
  POLL_ERROR: "POLL_ERROR",
  REFUND: "REFUND",
} as const;
export type LegEvent = (typeof LEG_EVENT)[keyof typeof LEG_EVENT];

const LEG_TABLE: Record<LegState, Partial<Record<LegEvent, LegState>>> = {
  [LEG.DRAFT]: {
    INITIATE: LEG.REQUESTED,
  },
  [LEG.REQUESTED]: {
    POLL_PENDING: LEG.PENDING,
    POLL_COMPLETED: LEG.POSTED,
    POLL_DECLINED: LEG.FAILED,
    POLL_CANCELLED: LEG.CANCELLED,
    POLL_ERROR: LEG.FAILED,
  },
  [LEG.PENDING]: {
    POLL_PENDING: LEG.PENDING,
    POLL_COMPLETED: LEG.POSTED,
    POLL_DECLINED: LEG.FAILED,
    POLL_CANCELLED: LEG.CANCELLED,
    POLL_ERROR: LEG.FAILED,
  },
  // POSTED is terminal except for a refund; a posted leg is money we hold
  [LEG.POSTED]: {
    REFUND: LEG.REFUNDED,
  },
  // Terminal. A failed or cancelled leg is never retried in place — the operator
  // removes it and tenders again, which produces a fresh leg with a fresh
  // paymentId. Retrying one would reuse the id the provider already rejected.
  [LEG.FAILED]: {},
  [LEG.CANCELLED]: {},
  [LEG.REFUNDED]: {},
};

/* ----------------------------------------------------------------- driving */

/** `null` means the event is not legal in that state — callers ignore it. */
export function nextSessionState(from: SessionState, event: SessionEvent): SessionState | null {
  return SESSION_TABLE[from]?.[event] ?? null;
}

export function nextLegState(from: LegState, event: LegEvent): LegState | null {
  return LEG_TABLE[from]?.[event] ?? null;
}

/** Terminal leg states hold no further obligation on the flow. */
export function isLegTerminal(state: LegState): boolean {
  return state === LEG.POSTED || state === LEG.REFUNDED;
}

/** A leg the session is still waiting on. */
export function isLegInFlight(state: LegState): boolean {
  return state === LEG.REQUESTED || state === LEG.PENDING;
}

/**
 * The provider status vocabulary (`payment/ext/status` → `res.state`) mapped
 * onto leg events. v1 does this with a switch duplicated in `handleGetExtCb`
 * and `handleRefreshGetExtCb`; anything unrecognised is a failure, as there.
 */
export function legEventForProviderState(providerState: string | undefined): LegEvent {
  switch ((providerState || "").toUpperCase()) {
    case "PENDING":
      return LEG_EVENT.POLL_PENDING;
    case "COMPLETED":
      return LEG_EVENT.POLL_COMPLETED;
    case "DECLINED":
      return LEG_EVENT.POLL_DECLINED;
    case "CANCELLED":
      return LEG_EVENT.POLL_CANCELLED;
    default:
      return LEG_EVENT.POLL_ERROR;
  }
}

/** Wire value written on the leg — what the backend and v1 both expect. */
export const LEG_STATE_TO_WIRE: Record<LegState, string | undefined> = {
  [LEG.DRAFT]: undefined,
  [LEG.REQUESTED]: "Requested",
  [LEG.PENDING]: "Pending",
  [LEG.POSTED]: "Posted",
  [LEG.FAILED]: "Failed",
  [LEG.CANCELLED]: "Failed",
  [LEG.REFUNDED]: "Refunded",
};

/**
 * Wire `paymentStatus` on a leg the SERVER sent back → the provider vocabulary,
 * so it can go through `legEventForProviderState` and land in the one reducer
 * that owns leg state.
 *
 * `undefined` means the status carries no news (`Requested`, or absent): the leg
 * is still in flight and nothing should transition. This is the inverse of
 * `LEG_STATE_TO_WIRE` and exists because `sale/payment/save` can return a leg
 * ALREADY TERMINAL (see ports.ts) — that response must move the machine, not be
 * merged past it.
 */
export function providerStateForWireStatus(status: string | undefined): string | undefined {
  switch (status) {
    case "Posted":
      return "COMPLETED";
    case "Failed":
      return "DECLINED";
    case "Pending":
      return "PENDING";
    default:
      return undefined;
  }
}
